// @flow
import React from 'react';
import { Strong } from '@finect/tabular-core/Strong';
import { AppLink } from '@finect/tabular-core/AppLink';
import { H2, H3 } from '@finect/tabular-core/Headings';
import { Paragraph } from '@finect/tabular-core/Paragraph';
import { ArticleDetail, ArticleCTA, LazyPill } from '../../../components';

type Props = {
  location: Object,
  history: Object,
  toggleModal: Function
};

export const Necesitas = ({ location, history, toggleModal }: Props) => {
  const redirectTo = (url: string) => {
    history.push(url);
  };

  return (
    <ArticleDetail
      location={location}
      title="¿Necesitas un asesor financiero? Descubre las claves"
      redirectTo={redirectTo}
      toggleModal={toggleModal}
      image="0301b301c52e429bad1599153b04b51f"
      schemaORG={{
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '¿Qué hace exactamente un asesor financiero?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Un asesor financiero es un profesional especializado en gestionar el dinero y las inversiones de sus clientes. Su función principal es ayudarte a tomar decisiones informadas sobre tus finanzas, adaptándose a tus necesidades y objetivos. Puede ofrecer servicios de planificación para la jubilación, optimización fiscal, diversificación de carteras, asesoramiento en seguros y mucho más. Su objetivo es maximizar el rendimiento de tus inversiones y proteger tu patrimonio en función de tu perfil de riesgo.'
            }
          },
          {
            '@type': 'Question',
            name: '¿En qué momentos de mi vida necesito un asesor financiero?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Necesitas un asesor financiero en momentos clave como recibir una herencia, empezar un nuevo trabajo, planificar la jubilación, comprar una vivienda o crear un negocio. También es especialmente útil en situaciones de incertidumbre económica o cuando quieres estructurar tu futuro financiero a largo plazo con una estrategia sólida.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Solo las personas con mucho dinero necesitan un asesor financiero?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'No. Aunque los grandes patrimonios suelen contar con asesores financieros, cualquier persona puede beneficiarse de sus servicios. Los asesores se adaptan a distintos perfiles y niveles de inversión: pueden ayudarte a optimizar tus ahorros, planificar la jubilación y hacer que tu dinero trabaje de forma eficiente, independientemente de tu nivel de ingresos o patrimonio.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Qué beneficios ofrece contar con un asesor financiero?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Contar con un asesor financiero te aporta tranquilidad, optimización de inversiones, diversificación de cartera y minimización de riesgos. Además, te guía en la planificación fiscal para aprovechar las ventajas disponibles y te ayuda a alcanzar tus objetivos a largo plazo con una estrategia personalizada y revisada de forma continua.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Cuál es la diferencia entre un asesor financiero y un coach financiero?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'La diferencia principal radica en el enfoque. Un asesor financiero se centra en la gestión de inversiones y finanzas, ofreciendo recomendaciones técnicas basadas en datos y análisis profesionales. Un coach financiero, en cambio, trabaja la educación financiera y el desarrollo de hábitos saludables con el dinero: ayuda a sus clientes a comprender su relación con las finanzas y a establecer metas, pero no ofrece asesoramiento específico sobre productos de inversión.'
            }
          }
        ]
      }}
    >
      <section>
        <Paragraph>
          Para nuestra salud, siempre recurrimos a un médico o a un farmacéutico; para arreglar el coche, nos viene de
          perlas tener un taller de confianza; si tenemos un juicio, buscamos un buen abogado… Si recurrimos de forma
          habitual a un experto en múltiples situaciones, ¿por qué no lo hacemos cuando se trata de algo tan importante
          como nuestras finanzas?
        </Paragraph>
        <Paragraph>
          Si alguna vez te has preguntado <Strong>"¿necesito un asesor financiero?"</Strong>, la respuesta corta es:
          casi con toda seguridad, sí. La figura de un{' '}
          <AppLink href="https://www.finect.com/asesores?utm_source=Finect&utm_medium=Articulo&utm_campaign=Asesores&utm_term=necesitas">
            asesor financiero
          </AppLink>{' '}
          que se preocupe por ti, que conozca tus metas y te ayude a alcanzarlas, puede ser de enorme ayuda para la
          gestión de tu dinero, tanto a la hora de preservarlo como de sacarle rentabilidad.
        </Paragraph>
        <Paragraph>
          Las grandes fortunas lo tienen claro. No administran ellas mismas su patrimonio, aunque muchas son expertas en
          negocios e inversiones, sino que cuentan con especialistas que les ayudan a organizarlo y tomar las mejores
          decisiones. Y saben que les compensa pagar por tener un profesional al frente de su cartera.
        </Paragraph>
        <Paragraph>
          Los pequeños inversores y ahorradores también necesitamos a alguien a nuestro lado. Solo por tener una segunda
          opinión y evitar los errores financieros más frecuentes, ya nos compensa. Incluso los inversores más avezados,
          quienes saben de inversiones y les gusta dedicar tiempo a seguirlas, se benefician de los servicios de un
          asesor financiero: para navegar entornos de incertidumbre y volatilidad, para organizar el futuro de sus
          finanzas o para preparar el legado de su familia.
        </Paragraph>
        <Paragraph>
          Todos deberíamos tener un asesor financiero o experto financiero a nuestro lado, igual que todos tenemos un
          médico de cabecera, un dentista o un mecánico. Alguien que de manera profesional se encargue de preservar y
          hacer crecer ese dinero que tanto nos cuesta ganar y ahorrar.
        </Paragraph>
        <Paragraph>
          Si te interesa pero no sabes por dónde empezar a buscar,{' '}
          <Strong>
            a través de Finect puedes encontrar{' '}
            <AppLink href="https://www.finect.com/asesores?utm_source=Finect&utm_medium=Articulo&utm_campaign=Asesores&utm_term=necesitas">
              asesores financieros y expertos financieros
            </AppLink>{' '}
            de forma completamente gratuita.
          </Strong>{' '}
          Para ello, solo debes rellenar{' '}
          <AppLink href="#" onClick={(e) => { e.preventDefault(); toggleModal(); }}>
            este formulario
          </AppLink>{' '}
          y te pondremos en contacto con los asesores que se ajusten a tu perfil y objetivos, para que hagan sus
          propuestas. Luego, podrás establecer relación con el que más encaje con tus necesidades y contratar sus
          servicios para que te ayuden con tus finanzas.
        </Paragraph>
        <Paragraph>La búsqueda de los mejores asesores financieros es totalmente gratuita para ti.</Paragraph>
      </section>

      <ArticleCTA targetId="solicita-asesor" toggleModal={toggleModal} />

      <section style={{ marginTop: '60px' }}>
        <H2>¿Merece la pena un asesor financiero? Diez razones por las que sí</H2>
        <Paragraph>
          Si todavía te preguntas si <Strong>merece la pena un asesor financiero</Strong>, estas diez razones te
          ayudarán a salir de dudas.
        </Paragraph>

        <H3>1. Experiencia y conocimiento especializado</H3>
        <Paragraph>
          Un asesor financiero posee los conocimientos y la experiencia necesaria para ayudarte a tomar decisiones
          informadas, adaptadas a tus necesidades. Con su apoyo, podrás gestionar tu dinero de forma eficiente y con
          criterio profesional.
        </Paragraph>

        <H3>2. Evitar errores comunes</H3>
        <Paragraph>
          A menudo, un inversor novato comete errores que cuestan dinero. Un asesor financiero puede ayudarte a evitar
          estas equivocaciones, asegurándose de que tu dinero trabaje para ti y no en tu contra.
        </Paragraph>

        <H3>3. Acompañamiento personalizado</H3>
        <Paragraph>
          Un buen asesor financiero se adapta a tu perfil y objetivos personales. Analiza tu situación actual, tus metas
          financieras y tu tolerancia al riesgo para diseñar una estrategia de inversión hecha a tu medida. Las
          recomendaciones no son genéricas: están alineadas con lo que realmente quieres alcanzar en tu vida financiera.
        </Paragraph>

        <H3>4. Planificación a largo plazo</H3>
        <Paragraph>
          No se trata solo de invertir dinero, sino de planificar el futuro. Un asesor te ayudará a diseñar un plan
          financiero para alcanzar tus objetivos a largo plazo, ya sea ahorrar para la jubilación, financiar la
          educación de tus hijos o comprar una vivienda.
        </Paragraph>

        <H3>5. Diversificación de inversiones</H3>
        <Paragraph>
          Un asesor financiero sabe cómo diversificar tus activos para reducir el riesgo. Esto es fundamental para
          proteger tu patrimonio en tiempos de incertidumbre económica y evitar que una mala racha en un sector lastre
          toda tu cartera.
        </Paragraph>

        <H3>6. Acceso a productos financieros exclusivos</H3>
        <Paragraph>
          Algunos productos financieros no están disponibles para el público general. Un asesor puede ofrecerte acceso a
          fondos de inversión o estrategias que, de otro modo, serían inaccesibles para un pequeño inversor.
        </Paragraph>

        <H3>7. Revisión constante de tu situación financiera</H3>
        <Paragraph>
          Un asesor no solo te ayuda a establecer un plan, sino que lo revisa periódicamente para asegurarse de que
          sigue siendo adecuado a medida que cambian tus circunstancias personales, familiares o del mercado.
        </Paragraph>

        <H3>8. Apoyo en momentos clave de la vida</H3>
        <Paragraph>
          Ya sea al recibir una herencia, al comenzar un nuevo trabajo o al preparar tu jubilación, un asesor puede
          guiarte en estos momentos críticos y asegurarse de que tomes las mejores decisiones para tu futuro financiero.
        </Paragraph>

        <H3>9. Optimización fiscal</H3>
        <Paragraph>
          Un buen asesor financiero no solo se enfoca en hacer crecer tu dinero, sino también en minimizar la carga
          fiscal. Te ayudará a organizar tus inversiones para aprovechar al máximo las ventajas fiscales disponibles y no
          pagar más de lo necesario.
        </Paragraph>

        <H3>10. Tranquilidad</H3>
        <Paragraph>
          Saber que tienes a alguien de confianza velando por tus finanzas te proporciona seguridad y paz mental. Puedes
          concentrarte en otros aspectos de tu vida sin preocuparte constantemente por el estado de tus ahorros e
          inversiones.
        </Paragraph>
      </section>

      <ArticleCTA targetId="solicita-asesor" toggleModal={toggleModal} />

      <section style={{ marginTop: '60px' }}>
        <H2>¿Cuándo es el momento adecuado para contratar un asesor?</H2>
        <Paragraph>
          Si aún no tienes claro si <Strong>necesitas un asesor financiero</Strong>, hay una serie de situaciones que
          son señales claras de que ha llegado el momento de dar el paso. El momento adecuado para contratar un asesor
          financiero puede depender de factores relacionados con tu situación financiera y tus objetivos, pero en
          ciertos momentos clave, tener un experto financiero a tu lado puede ser más útil.
        </Paragraph>
        <Paragraph>
          Si sientes que no tienes el tiempo o los conocimientos suficientes para gestionar tu dinero, ese es el primer
          indicio. Aunque puedas cubrir lo básico, sin la capacidad o el tiempo para analizar el mercado en profundidad,
          corres el riesgo de tomar decisiones precipitadas o incompletas.
        </Paragraph>
        <Paragraph>
          Si tu patrimonio ha crecido hasta cierto umbral, contar con un asesor se vuelve esencial. Los ahorros e
          inversiones de cierto volumen requieren planificación detallada y estrategias que maximicen el rendimiento y
          minimicen los riesgos de forma simultánea.
        </Paragraph>
        <Paragraph>
          Si tus objetivos financieros son a largo plazo -la jubilación, la educación de tus hijos, la compra de una
          vivienda- un asesor financiero te ayudará a establecer metas claras y a trazar el camino para alcanzarlas con
          coherencia y sin improvisaciones.
        </Paragraph>
        <Paragraph>
          Cuando te enfrentas a una situación financiera compleja -una herencia, un cambio de empleo, el inicio de un
          negocio- la orientación de un profesional puede marcar la diferencia entre una decisión inteligente y un error
          costoso.
        </Paragraph>
        <Paragraph>
          Si tienes tendencia a tomar decisiones impulsivas, un asesor aporta el enfoque racional y externo que
          necesitas: análisis basado en datos, no en miedos o emociones del momento.
        </Paragraph>
        <Paragraph>
          Y si llevas tiempo invirtiendo por tu cuenta pero no ves los resultados esperados, un asesor puede revisar tu
          estrategia, ajustar las inversiones y mejorar el rendimiento de tu cartera. Si eres nuevo en el mundo de la
          inversión, también: te enseñará los fundamentos y te ayudará a empezar con criterio.
        </Paragraph>
      </section>

      <ArticleCTA targetId="solicita-asesor" toggleModal={toggleModal} />

      <section style={{ marginTop: '60px' }}>
        <H2>Preguntas frecuentes</H2>
        <H3>1. ¿Qué hace exactamente un asesor financiero?</H3>
        <Paragraph>
          Un asesor financiero es un profesional especializado en gestionar el dinero y las inversiones de sus clientes.
          Su función principal es ayudarte a tomar decisiones informadas sobre tus finanzas, adaptándose a tus
          necesidades y objetivos. Puede ofrecer servicios de planificación para la jubilación, optimización fiscal,
          diversificación de carteras, asesoramiento en seguros y mucho más. Su objetivo es maximizar el rendimiento de
          tus inversiones y proteger tu patrimonio en función de tu perfil de riesgo.
        </Paragraph>

        <H3>2. ¿En qué momentos de mi vida necesito un asesor financiero?</H3>
        <Paragraph>
          Necesitas un asesor financiero en momentos clave como recibir una herencia, empezar un nuevo trabajo,
          planificar la jubilación, comprar una vivienda o crear un negocio. También es especialmente útil en
          situaciones de incertidumbre económica o cuando quieres estructurar tu futuro financiero a largo plazo con una
          estrategia sólida.
        </Paragraph>

        <H3>3. ¿Solo las personas con mucho dinero necesitan un asesor financiero?</H3>
        <Paragraph>
          No. Aunque los grandes patrimonios suelen contar con asesores financieros, cualquier persona puede
          beneficiarse de sus servicios. Los asesores se adaptan a distintos perfiles y niveles de inversión: pueden
          ayudarte a optimizar tus ahorros, planificar la jubilación y hacer que tu dinero trabaje de forma eficiente,
          independientemente de tu nivel de ingresos o patrimonio.
        </Paragraph>

        <H3>4. ¿Qué beneficios ofrece contar con un asesor financiero?</H3>
        <Paragraph>
          Contar con un asesor financiero te aporta tranquilidad, optimización de inversiones, diversificación de
          cartera y minimización de riesgos. Además, te guía en la planificación fiscal para aprovechar las ventajas
          disponibles y te ayuda a alcanzar tus objetivos a largo plazo con una estrategia personalizada y revisada de
          forma continua.
        </Paragraph>

        <H3>5. ¿Cuál es la diferencia entre un asesor financiero y un coach financiero?</H3>
        <Paragraph>
          La diferencia principal radica en el enfoque. Un asesor financiero se centra en la gestión de inversiones y
          finanzas, ofreciendo recomendaciones técnicas basadas en datos y análisis profesionales. Un coach financiero,
          en cambio, trabaja la educación financiera y el desarrollo de hábitos saludables con el dinero: ayuda a sus
          clientes a comprender su relación con las finanzas y a establecer metas, pero no ofrece asesoramiento
          específico sobre productos de inversión.
        </Paragraph>

        <hr />

        <H2>Da el primer paso</H2>
        <Paragraph>
          Si te interesa encontrar un asesor financiero pero no sabes por dónde empezar, a través de Finect puedes
          encontrar asesores y expertos financieros de forma completamente gratuita. Solo tienes que rellenar el
          formulario y te pondremos en contacto con los profesionales que mejor se ajusten a tu perfil y objetivos, para
          que presenten sus propuestas. Luego, podrás elegir el que mejor encaje con tus necesidades.
        </Paragraph>
        <Paragraph>
          <Strong>La búsqueda de los mejores asesores financieros es totalmente gratuita para ti.</Strong>
        </Paragraph>

        <LazyPill />
      </section>
    </ArticleDetail>
  );
};
